/**
 * External dependencies
 */
// eslint-disable-next-line no-restricted-imports
import * as Ariakit from '@ariakit/react';
import removeAccents from 'remove-accents';

/**
 * WordPress dependencies
 */
import { __, sprintf } from '@wordpress/i18n';
import { useState, useMemo, useDeferredValue } from '@wordpress/element';
import { VisuallyHidden, Icon, privateApis as componentsPrivateApis } from '@wordpress/components';
import { search, check } from '@wordpress/icons';
import { SVG, Circle } from '@wordpress/primitives';

/**
 * Internal dependencies
 */
import { unlock } from './lock-unlock';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const {
  CompositeV2: Composite,
  CompositeItemV2: CompositeItem,
  useCompositeStoreV2: useCompositeStore
} = unlock(componentsPrivateApis);
const radioCheck = /*#__PURE__*/_jsx(SVG, {
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 24 24",
  children: /*#__PURE__*/_jsx(Circle, {
    cx: 12,
    cy: 12,
    r: 3
  })
});
function normalizeSearchInput(input = '') {
  return removeAccents(input.trim().toLowerCase());
}
const EMPTY_ARRAY = [];
const getCurrentValue = (filterDefinition, currentFilter) => {
  if (filterDefinition.singleSelection) {
    return currentFilter?.value;
  }
  if (Array.isArray(currentFilter?.value)) {
    return currentFilter.value;
  }
  if (!Array.isArray(currentFilter?.value) && !!currentFilter?.value) {
    return [currentFilter.value];
  }
  return EMPTY_ARRAY;
};
const getNewValue = (filterDefinition, currentFilter, value) => {
  if (filterDefinition.singleSelection) {
    return value;
  }
  if (Array.isArray(currentFilter?.value)) {
    return currentFilter.value.includes(value) ? currentFilter.value.filter(v => v !== value) : [...currentFilter.value, value];
  }
  return [value];
};
function ListBox({
  view,
  filter,
  onChangeView
}) {
  const compositeStore = useCompositeStore({
    virtualFocus: true,
    focusLoop: true,
    // When we have no or just one operator, we can set the first item as active.
    // We do that by passing `undefined` to `defaultActiveId`. Otherwise, we set it to `null`,
    // so the first item is not selected, since the focus is on the operators control.
    defaultActiveId: filter.operators?.length === 1 ? undefined : null
  });
  const currentFilter = view.filters?.find(f => f.field === filter.field);
  const currentValue = getCurrentValue(filter, currentFilter);
  return /*#__PURE__*/_jsx(Composite, {
    store: compositeStore,
    role: "listbox",
    className: "dataviews-search-widget-listbox",
    "aria-label": sprintf( /* translators: List of items for a filter. 1: Filter name. e.g.: "List of: Author". */
    __('List of: %1$s'), filter.name),
    onFocusVisible: () => {
      if (!compositeStore.getState().activeId) {
        compositeStore.move(compositeStore.first());
      }
    },
    render: /*#__PURE__*/_jsx(Ariakit.CompositeTypeahead, {
      store: compositeStore
    }),
    children: filter.elements.map(element => /*#__PURE__*/_jsxs(Ariakit.CompositeHover, {
      store: compositeStore,
      render: /*#__PURE__*/_jsx(CompositeItem, {
        render: /*#__PURE__*/_jsx("div", {
          "aria-label": element.label,
          role: "option",
          className: "dataviews-search-widget-listitem"
        }),
        onClick: () => {
          var _view$filters, _view$filters2;
          const newFilters = currentFilter ? [...((_view$filters = view.filters) !== null && _view$filters !== void 0 ? _view$filters : []).map(_filter => {
            if (_filter.field === filter.field) {
              return {
                ..._filter,
                operator: currentFilter.operator || filter.operators[0],
                value: getNewValue(filter, currentFilter, element.value)
              };
            }
            return _filter;
          })] : [...((_view$filters2 = view.filters) !== null && _view$filters2 !== void 0 ? _view$filters2 : []), {
            field: filter.field,
            operator: filter.operators[0],
            value: getNewValue(filter, currentFilter, element.value)
          }];
          onChangeView({
            ...view,
            page: 1,
            filters: newFilters
          });
        }
      }),
      children: [/*#__PURE__*/_jsxs("span", {
        className: "dataviews-search-widget-listitem-check",
        children: [filter.singleSelection && currentValue === element.value && /*#__PURE__*/_jsx(Icon, {
          icon: radioCheck
        }), !filter.singleSelection && currentValue.includes(element.value) && /*#__PURE__*/_jsx(Icon, {
          icon: check
        })]
      }), /*#__PURE__*/_jsxs("span", {
        children: [element.label, !!element.description && /*#__PURE__*/_jsx("span", {
          className: "dataviews-search-widget-listitem-description",
          children: element.description
        })]
      })]
    }, element.value))
  });
}
function ComboboxList({
  view,
  filter,
  onChangeView
}) {
  const [searchValue, setSearchValue] = useState('');
  const deferredSearchValue = useDeferredValue(searchValue);
  const currentFilter = view.filters?.find(_filter => _filter.field === filter.field);
  const currentValue = getCurrentValue(filter, currentFilter);
  const matches = useMemo(() => {
    const normalizedSearch = normalizeSearchInput(deferredSearchValue);
    return filter.elements.filter(item => normalizeSearchInput(item.label).includes(normalizedSearch));
  }, [filter.elements, deferredSearchValue]);
  return /*#__PURE__*/_jsxs(Ariakit.ComboboxProvider, {
    resetValueOnSelect: false,
    selectedValue: currentValue,
    setSelectedValue: value => {
      var _view$filters3, _view$filters4;
      const newFilters = currentFilter ? [...((_view$filters3 = view.filters) !== null && _view$filters3 !== void 0 ? _view$filters3 : []).map(_filter => {
        if (_filter.field === filter.field) {
          return {
            ..._filter,
            operator: currentFilter.operator || filter.operators[0],
            value
          };
        }
        return _filter;
      })] : [...((_view$filters4 = view.filters) !== null && _view$filters4 !== void 0 ? _view$filters4 : []), {
        field: filter.field,
        operator: filter.operators[0],
        value
      }];
      onChangeView({
        ...view,
        page: 1,
        filters: newFilters
      });
    },
    setValue: setSearchValue,
    children: [/*#__PURE__*/_jsxs("div", {
      className: "dataviews-search-widget-filter-combobox__wrapper",
      children: [/*#__PURE__*/_jsx(Ariakit.ComboboxLabel, {
        render: /*#__PURE__*/_jsx(VisuallyHidden, {
          children: __('Search items')
        }),
        children: __('Search items')
      }), /*#__PURE__*/_jsx(Ariakit.Combobox, {
        autoSelect: "always",
        placeholder: __('Search'),
        className: "dataviews-search-widget-filter-combobox__input"
      }), /*#__PURE__*/_jsx("div", {
        className: "dataviews-search-widget-filter-combobox__icon",
        children: /*#__PURE__*/_jsx(Icon, {
          icon: search
        })
      })]
    }), /*#__PURE__*/_jsxs(Ariakit.ComboboxList, {
      className: "dataviews-search-widget-filter-combobox-list",
      alwaysVisible: true,
      children: [matches.map(element => {
        return /*#__PURE__*/_jsxs(Ariakit.ComboboxItem, {
          value: element.value,
          className: "dataviews-search-widget-listitem",
          hideOnClick: false,
          setValueOnClick: false,
          focusOnHover: true,
          children: [/*#__PURE__*/_jsxs("span", {
            className: "dataviews-search-widget-listitem-check",
            children: [filter.singleSelection && currentValue === element.value && /*#__PURE__*/_jsx(Icon, {
              icon: radioCheck
            }), !filter.singleSelection && currentValue.includes(element.value) && /*#__PURE__*/_jsx(Icon, {
              icon: check
            })]
          }), /*#__PURE__*/_jsxs("span", {
            children: [/*#__PURE__*/_jsx(Ariakit.ComboboxItemValue, {
              className: "dataviews-search-widget-filter-combobox-item-value",
              value: element.label
            }), !!element.description && /*#__PURE__*/_jsx("span", {
              className: "dataviews-search-widget-listitem-description",
              children: element.description
            })]
          })]
        }, element.value);
      }), !matches.length && /*#__PURE__*/_jsx("p", {
        children: __('No results found')
      })]
    })]
  });
}
export default function SearchWidget(props) {
  const Widget = props.filter.elements.length > 10 ? ComboboxList : ListBox;
  return /*#__PURE__*/_jsx(Widget, {
    ...props
  });
}
//# sourceMappingURL=search-widget.js.map